import { mulberry32 } from "../meta/rng";
import type { MissionConfig } from "./types";

// SIGINT Analysis (docs/design/HUB.md §Room 2): before hard missions, pick
// which of three intercepted fragments is actionable. Right = REDFOR loses
// surprise (an ambush anchor is fixed and converts to garrison) or their
// reserve tasking is read (reinforcement budget dips). Wrong = nothing —
// no punishment, per the doc. Pure over MissionConfig; the flow applies it.

export type SigintPuzzle = {
  fragments: string[]; // three intercepts, display order
  correctIndex: number;
};

// The actionable fragment references what the mission actually holds; the
// decoys are plausible sector noise.
const DECOYS: readonly string[] = [
  "…requesting resupply of winter socks, over… say again… socks…",
  "…battalion kitchen reports the goulash situation resolved…",
  "…routine patrol, grid empty, returning to barracks by 0400…",
  "…radio check… radio check… is this thing even…",
  "…political officer to deliver remarks at morning formation…",
  "…motor pool reports two trucks down for maintenance until further notice…",
];

const AMBUSH_TELLS: readonly string[] = [
  "…hold positions until the escort is committed… let them pass the marker, then fire…",
  "…do not engage early… wait for my signal… surprise is everything, comrades…",
  "…conceal the positions before the aircraft arrives… no fires, no smoking…",
];

const RESERVE_TELLS: readonly string[] = [
  "…reserve company to stage at the depot… commit on my order only…",
  "…reinforcements released to sector command… awaiting tasking…",
];

function hasAmbushAnchor(config: MissionConfig): boolean {
  return (config.enemyAnchors ?? []).some((a) => a.posture === "ambush");
}

export function buildSigintPuzzle(config: MissionConfig, seed: number): SigintPuzzle {
  const rng = mulberry32(seed >>> 0);
  const tells = hasAmbushAnchor(config) ? AMBUSH_TELLS : RESERVE_TELLS;
  const correct = tells[Math.floor(rng() * tells.length) % tells.length]!;
  const decoyPool = [...DECOYS];
  const decoys: string[] = [];
  while (decoys.length < 2) {
    const i = Math.floor(rng() * decoyPool.length) % decoyPool.length;
    decoys.push(decoyPool.splice(i, 1)[0]!);
  }
  const correctIndex = Math.floor(rng() * 3) % 3;
  const fragments = [...decoys];
  fragments.splice(correctIndex, 0, correct);
  return { fragments, correctIndex };
}

// The reward for reading the traffic right. Mutates the config the flow is
// about to launch (this is pre-launch configuration, not mid-mission meta).
export function applySigintReward(config: MissionConfig): string {
  const ambush = (config.enemyAnchors ?? []).find((a) => a.posture === "ambush");
  if (ambush) {
    ambush.posture = "garrison";
    const line = "SIGINT: an ambush position is fixed — they've lost the element of surprise.";
    config.briefing = [...(config.briefing ?? []), line];
    return line;
  }
  config.reinforcementBudget = Math.max(0, (config.reinforcementBudget ?? 10) - 3);
  const line = "SIGINT: REDFOR reserve tasking intercepted — their reinforcements thin out.";
  config.briefing = [...(config.briefing ?? []), line];
  return line;
}
